/**
 * Createur de bonhomme: apercu 3D qui tourne d'un cote, catalogue de l'autre.
 *
 * C'est le premier ecran de la partie, et aussi le miroir de la maison en mode
 * edition. La petite scene three.js lui appartient entierement: elle est creee
 * a l'ouverture et entierement liberee a la fermeture, car le panneau peut
 * etre ouvert et referme un grand nombre de fois.
 */
import '../styles/creation.css'
import * as THREE from 'three'
import type { Apparence, Corps } from '../../shared/types/player.ts'
import {
  CATALOGUE,
  NOMS_CORPS,
  PALETTE_CHEVEUX,
  PALETTE_PEAU,
  PALETTE_VETEMENT,
  apparenceAleatoire,
  apparenceParDefaut,
  validerApparence,
} from '../../shared/data/apparence.ts'
import type { Bonhomme } from '../character/bonhomme.ts'
import { creerApercu } from '../character/bonhomme.ts'
import { Alea } from '../../shared/sim/alea.ts'
import { journal } from '../log/journal.ts'
import { bouton, champCouleur, clicSonore, el, hex, pastilleCouleur, refusSonore } from './commun.ts'

/** Categories du catalogue: ce sont exactement les clefs de CATALOGUE. */
type Categorie = 'cheveux' | 'yeux' | 'barbe' | 'chapeau' | 'haut' | 'bas' | 'souliers' | 'accessoire'

/** Onglets du createur: le corps et la peau viennent avant le catalogue. */
type Onglet = 'corps' | 'peau' | Categorie

const ONGLETS: readonly { id: Onglet; nom: string; icone: string }[] = [
  { id: 'corps', nom: 'Corps', icone: '🧍' },
  { id: 'peau', nom: 'Peau', icone: '🎨' },
  { id: 'cheveux', nom: 'Cheveux', icone: '💇' },
  { id: 'yeux', nom: 'Yeux', icone: '👀' },
  { id: 'barbe', nom: 'Barbe', icone: '🧔' },
  { id: 'chapeau', nom: 'Chapeau', icone: '🎩' },
  { id: 'haut', nom: 'Haut', icone: '👕' },
  { id: 'bas', nom: 'Bas', icone: '👖' },
  { id: 'souliers', nom: 'Souliers', icone: '👟' },
  { id: 'accessoire', nom: 'Accessoire', icone: '🎒' },
]

/** Champ d'Apparence qui porte la couleur de chaque categorie. */
const CHAMP_COULEUR = {
  cheveux: 'cheveuxCouleur',
  yeux: 'yeuxCouleur',
  barbe: 'barbeCouleur',
  chapeau: 'chapeauCouleur',
  haut: 'hautCouleur',
  bas: 'basCouleur',
  souliers: 'souliersCouleur',
  accessoire: 'accessoireCouleur',
} as const

/** Palette proposee par categorie. Les poils suivent la palette de cheveux. */
const PALETTES: Readonly<Record<Categorie, readonly number[]>> = {
  cheveux: PALETTE_CHEVEUX,
  yeux: PALETTE_CHEVEUX,
  barbe: PALETTE_CHEVEUX,
  chapeau: PALETTE_VETEMENT,
  haut: PALETTE_VETEMENT,
  bas: PALETTE_VETEMENT,
  souliers: PALETTE_VETEMENT,
  accessoire: PALETTE_VETEMENT,
}

const CORPS: readonly { id: Corps; icone: string }[] = [
  { id: 'homme', icone: '👨' },
  { id: 'femme', icone: '👩' },
  { id: 'neutre', icone: '🧑' },
]

/** Longueur maximale du nom du bonhomme. */
const NOM_MAX = 20

/** Vitesse de rotation automatique de l'apercu, en radians par seconde. */
const VITESSE_TOURNE = 0.55

/** Duree de pause de la rotation automatique apres un glissement, en ms. */
const PAUSE_APRES_GLISSEMENT = 2500

export interface ResultatCreation {
  nom: string
  apparence: Apparence
}

interface Moteur {
  toile: HTMLCanvasElement
  rendu: THREE.WebGLRenderer
  scene: THREE.Scene
  camera: THREE.PerspectiveCamera
  bonhomme: Bonhomme
  groupe: THREE.Group
  pivot: THREE.Group
  observateur: ResizeObserver
  image: number
  dernier: number
  angle: number
  pauseJusqua: number
}

export class Creation {
  private readonly parent: HTMLElement
  private racine: HTMLDivElement | null = null
  private contenu: HTMLDivElement | null = null
  private champNom: HTMLInputElement | null = null
  private erreurNom: HTMLDivElement | null = null
  private onglets: HTMLDivElement | null = null
  private moteur: Moteur | null = null

  private apparence: Apparence = apparenceParDefaut()
  private nom = ''
  private edition = false
  private actif: Onglet = 'corps'
  private resoudre: ((v: ResultatCreation | null) => void) | null = null

  /** Boutons de la vue courante, pour ne pas tout reconstruire a chaque clic. */
  private optionsBtn: { e: HTMLElement; i: number }[] = []
  private pastilles: { e: HTMLElement; c: number }[] = []

  private readonly surClavier = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.edition) {
      e.preventDefault()
      journal.input('creation', 'annulation par Echap')
      this.terminer(null)
    }
  }

  constructor(parent: HTMLElement) {
    this.parent = parent
  }

  get ouvert(): boolean {
    return this.racine !== null
  }

  /**
   * Ouvre le createur. Resout au clic sur "Jouer" / "Valider", ou avec null si
   * l'utilisateur annule (seulement possible en mode edition).
   */
  ouvrir(
    apparenceInitiale: Apparence,
    nomInitial: string,
    modeEdition: boolean,
  ): Promise<ResultatCreation | null> {
    if (this.racine) {
      journal.warn('creation', 'ouverture demandee alors que le createur est deja ouvert')
      return Promise.resolve(null)
    }
    this.apparence = validerApparence(apparenceInitiale)
    this.nom = nomInitial.slice(0, NOM_MAX)
    this.edition = modeEdition
    this.actif = 'corps'
    journal.input('creation', `ouverture du createur (${modeEdition ? 'edition' : 'nouvelle partie'})`, {
      nom: this.nom,
    })
    this.construire()
    window.addEventListener('keydown', this.surClavier)
    return new Promise<ResultatCreation | null>((r) => {
      this.resoudre = r
    })
  }

  // ------------------------------------------------------------- structure

  private construire(): void {
    const racine = el('div', 'creation')
    this.racine = racine

    // ------------------------------------------------------------ apercu
    const scene = el('div', 'creation__scene')
    const toile = el('canvas', 'creation__toile')
    scene.append(toile)
    scene.append(el('div', 'creation__astuce', 'Glissez pour faire tourner'))
    racine.append(scene)

    // ----------------------------------------------------------- panneau
    const panneau = el('div', 'creation__panneau')

    const entete = el('div', 'creation__entete')
    entete.append(el('span', 'creation__emoji', '✨'))
    entete.append(
      el('h1', 'creation__titre', this.edition ? 'Change ton allure' : 'Crée ton bonhomme'),
    )
    if (this.edition) {
      const x = el('button', 'creation__fermer', '✕')
      x.type = 'button'
      x.title = 'Annuler'
      x.setAttribute('aria-label', 'Annuler')
      x.addEventListener('click', () => {
        clicSonore()
        journal.input('creation', 'annulation par le bouton')
        this.terminer(null)
      })
      entete.append(x)
    }
    panneau.append(entete)

    // -------------------------------------------------------------- nom
    const ligneNom = el('div', 'creation__nom')
    const etiquette = el('label', 'creation__etiquette', 'Nom')
    etiquette.htmlFor = 'creation-nom'
    const champ = el('input', 'creation__champ')
    champ.id = 'creation-nom'
    champ.type = 'text'
    champ.maxLength = NOM_MAX
    champ.value = this.nom
    champ.placeholder = 'Ton nom…'
    champ.autocomplete = 'off'
    champ.spellcheck = false
    champ.readOnly = this.edition
    if (this.edition) champ.title = 'Le nom ne se change pas en cours de partie'
    champ.addEventListener('input', () => {
      this.nom = champ.value.slice(0, NOM_MAX)
      if (this.nom.trim().length > 0) this.cacherErreur()
    })
    champ.addEventListener('change', () => journal.input('creation', `nom=${this.nom}`))
    this.champNom = champ
    const erreur = el('div', 'creation__erreur')
    erreur.hidden = true
    this.erreurNom = erreur
    ligneNom.append(etiquette, champ)
    panneau.append(ligneNom, erreur)

    // ---------------------------------------------------------- onglets
    const onglets = el('div', 'creation__onglets')
    onglets.setAttribute('role', 'tablist')
    for (const o of ONGLETS) {
      const b = el('button', 'onglet')
      b.type = 'button'
      b.setAttribute('role', 'tab')
      b.dataset['onglet'] = o.id
      b.append(el('span', 'onglet__icone', o.icone))
      b.append(el('span', 'onglet__texte', o.nom))
      b.addEventListener('click', () => {
        clicSonore()
        this.actif = o.id
        journal.input('creation', `onglet=${o.id}`)
        this.majOnglets()
        this.majContenu()
      })
      onglets.append(b)
    }
    this.onglets = onglets
    panneau.append(onglets)

    // ---------------------------------------------------------- contenu
    const contenu = el('div', 'creation__contenu')
    this.contenu = contenu
    panneau.append(contenu)

    // ------------------------------------------------------------- pied
    const pied = el('div', 'creation__pied')
    pied.append(
      bouton('🎲 Aléatoire', () => this.tirerAuHasard(), 'bouton--sombre creation__hasard'),
    )
    pied.append(
      bouton(
        this.edition ? '✔ Valider' : '▶ Jouer',
        () => this.valider(),
        'bouton--vert creation__jouer',
      ),
    )
    panneau.append(pied)

    racine.append(panneau)
    this.parent.append(racine)

    this.majOnglets()
    this.majContenu()
    this.demarrerApercu(toile, scene)

    requestAnimationFrame(() => racine.classList.add('creation--ouvert'))
    if (!this.edition) champ.focus({ preventScroll: true })
  }

  // -------------------------------------------------------------- onglets

  private majOnglets(): void {
    if (!this.onglets) return
    for (const b of Array.from(this.onglets.children)) {
      const est = b instanceof HTMLElement && b.dataset['onglet'] === this.actif
      b.classList.toggle('onglet--actif', est)
      if (b instanceof HTMLElement) b.setAttribute('aria-selected', est ? 'true' : 'false')
    }
  }

  private majContenu(): void {
    const c = this.contenu
    if (!c) return
    c.replaceChildren()
    this.optionsBtn = []
    this.pastilles = []
    if (this.actif === 'corps') this.vueCorps(c)
    else if (this.actif === 'peau') this.vuePeau(c)
    else this.vueCategorie(c, this.actif)
    c.scrollTop = 0
  }

  private vueCorps(c: HTMLElement): void {
    c.append(el('p', 'creation__aide', 'Choisis la silhouette de ton bonhomme.'))
    const grille = el('div', 'choix choix--corps')
    for (const co of CORPS) {
      const b = el('button', 'choix__carte')
      b.type = 'button'
      b.append(el('span', 'choix__icone', co.icone))
      b.append(el('span', 'choix__nom', NOMS_CORPS[co.id]))
      if (this.apparence.corps === co.id) b.classList.add('choix__carte--actif')
      b.addEventListener('click', () => {
        clicSonore()
        this.apparence = { ...this.apparence, corps: co.id }
        journal.input('creation', `corps=${co.id}`)
        this.appliquer()
        for (const autre of Array.from(grille.children)) autre.classList.remove('choix__carte--actif')
        b.classList.add('choix__carte--actif')
      })
      grille.append(b)
    }
    c.append(grille)
  }

  private vuePeau(c: HTMLElement): void {
    c.append(el('p', 'creation__aide', 'La teinte de peau de ton bonhomme.'))
    c.append(this.zoneCouleurs(PALETTE_PEAU, this.apparence.peau, (couleur) => {
      this.apparence = { ...this.apparence, peau: couleur }
      journal.input('creation', `peau=${hex(couleur)}`)
      this.appliquer()
    }))
  }

  private vueCategorie(c: HTMLElement, cat: Categorie): void {
    const liste = CATALOGUE[cat]
    const choix = el('div', 'choix')
    for (const o of liste) {
      const b = el('button', 'choix__carte choix__carte--texte')
      b.type = 'button'
      b.append(el('span', 'choix__nom', o.nom))
      if (this.apparence[cat] === o.id) b.classList.add('choix__carte--actif')
      b.addEventListener('click', () => {
        clicSonore()
        const suite: Apparence = { ...this.apparence }
        suite[cat] = o.id
        this.apparence = suite
        journal.input('creation', `${cat}=${o.id} (${o.nom}) couleur=${hex(this.couleurDe(cat))}`)
        this.appliquer()
        for (const { e, i } of this.optionsBtn) e.classList.toggle('choix__carte--actif', i === o.id)
      })
      choix.append(b)
      this.optionsBtn.push({ e: b, i: o.id })
    }
    c.append(choix)

    const titre = el('div', 'creation__sous-titre', 'Couleur')
    c.append(titre)
    c.append(
      this.zoneCouleurs(PALETTES[cat], this.couleurDe(cat), (couleur) => {
        const suite: Apparence = { ...this.apparence }
        suite[CHAMP_COULEUR[cat]] = couleur
        this.apparence = suite
        journal.input('creation', `${cat}=${this.apparence[cat]} couleur=${hex(couleur)}`)
        this.appliquer()
      }),
    )
  }

  /** Rangee de pastilles + champ de couleur libre. */
  private zoneCouleurs(
    palette: readonly number[],
    courante: number,
    poser: (c: number) => void,
  ): HTMLElement {
    const zone = el('div', 'couleurs')
    const appliquer = (couleur: number): void => {
      poser(couleur)
      for (const { e, c } of this.pastilles) {
        const est = c === couleur
        e.classList.toggle('pastille--active', est)
        e.setAttribute('aria-pressed', est ? 'true' : 'false')
      }
    }
    for (const couleur of palette) {
      const p = pastilleCouleur(couleur, couleur === courante, () => appliquer(couleur))
      this.pastilles.push({ e: p, c: couleur })
      zone.append(p)
    }
    zone.append(champCouleur(courante, (c) => appliquer(c)))
    return zone
  }

  private couleurDe(cat: Categorie): number {
    return this.apparence[CHAMP_COULEUR[cat]]
  }

  // -------------------------------------------------------------- actions

  private tirerAuHasard(): void {
    const graine = (Date.now() ^ Math.floor(Math.random() * 0xffffffff)) >>> 0
    this.apparence = apparenceAleatoire(new Alea(graine))
    journal.input('creation', 'aleatoire', { graine, apparence: this.apparence })
    this.appliquer()
    this.majContenu()
  }

  private valider(): void {
    const nom = this.nom.trim()
    if (nom.length === 0) {
      refusSonore()
      this.montrerErreur('Écris un nom pour ton bonhomme, s’il te plaît.')
      this.champNom?.focus()
      journal.input('creation', 'validation refusee: nom vide')
      return
    }
    journal.input('creation', `validation nom=${nom}`, { apparence: this.apparence })
    this.terminer({ nom, apparence: this.apparence })
  }

  private montrerErreur(texte: string): void {
    if (!this.erreurNom) return
    this.erreurNom.textContent = texte
    this.erreurNom.hidden = false
    this.champNom?.classList.add('creation__champ--erreur')
  }

  private cacherErreur(): void {
    if (!this.erreurNom) return
    this.erreurNom.hidden = true
    this.champNom?.classList.remove('creation__champ--erreur')
  }

  private appliquer(): void {
    this.moteur?.bonhomme.appliquer(this.apparence)
  }

  private terminer(v: ResultatCreation | null): void {
    const r = this.resoudre
    this.resoudre = null
    this.fermer()
    r?.(v)
  }

  /** Ferme et libere tout: three.js, ecouteurs, DOM. */
  fermer(): void {
    window.removeEventListener('keydown', this.surClavier)
    this.arreterApercu()
    const racine = this.racine
    this.racine = null
    this.contenu = null
    this.champNom = null
    this.erreurNom = null
    this.onglets = null
    this.optionsBtn = []
    this.pastilles = []
    if (racine) {
      racine.classList.remove('creation--ouvert')
      window.setTimeout(() => racine.remove(), 200)
    }
    journal.input('creation', 'createur ferme')
    const r = this.resoudre
    this.resoudre = null
    r?.(null)
  }

  /** Comme `fermer`, mais le DOM part tout de suite, sans fondu. */
  detruire(): void {
    const racine = this.racine
    this.fermer()
    racine?.remove()
  }

  // --------------------------------------------------------------- apercu

  private demarrerApercu(toile: HTMLCanvasElement, conteneur: HTMLElement): void {
    let rendu: THREE.WebGLRenderer
    try {
      rendu = new THREE.WebGLRenderer({ canvas: toile, antialias: true, alpha: true })
    } catch (e) {
      journal.error('creation', 'apercu 3D indisponible', { erreur: String(e) })
      conteneur.classList.add('creation__scene--sans-3d')
      return
    }
    rendu.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    rendu.shadowMap.enabled = false

    const scene = new THREE.Scene()
    // La perspective n'est permise qu'ici: c'est plus flatteur pour un portrait.
    const camera = new THREE.PerspectiveCamera(30, 1, 0.1, 50)
    camera.position.set(0, 1.15, 3.4)
    camera.lookAt(0, 0.95, 0)

    scene.add(new THREE.HemisphereLight(0xdfeeff, 0x30425a, 1.1))
    const cle = new THREE.DirectionalLight(0xfff3d6, 1.5)
    cle.position.set(2.2, 3.4, 2.8)
    scene.add(cle)
    const appoint = new THREE.DirectionalLight(0x8fc7ff, 0.55)
    appoint.position.set(-2.6, 1.6, -1.8)
    scene.add(appoint)

    // Le socle sous les pieds vient deja de creerApercu; le halo est en CSS.
    const { groupe, bonhomme } = creerApercu(this.apparence)
    const pivot = new THREE.Group()
    pivot.add(groupe)
    scene.add(pivot)

    const observateur = new ResizeObserver(() => this.redimensionner(conteneur))
    observateur.observe(conteneur)

    this.moteur = {
      toile,
      rendu,
      scene,
      camera,
      bonhomme,
      groupe,
      pivot,
      observateur,
      image: 0,
      dernier: performance.now(),
      angle: 0.35,
      pauseJusqua: 0,
    }
    this.redimensionner(conteneur)
    this.brancherGlissement(toile)

    const boucle = (maintenant: number): void => {
      const m = this.moteur
      if (!m) return
      m.image = requestAnimationFrame(boucle)
      const dt = Math.min(0.1, (maintenant - m.dernier) / 1000)
      m.dernier = maintenant
      if (maintenant > m.pauseJusqua) m.angle += VITESSE_TOURNE * dt
      m.pivot.rotation.y = m.angle
      m.rendu.render(m.scene, m.camera)
    }
    this.moteur.image = requestAnimationFrame(boucle)
    journal.info('creation', 'apercu 3D demarre')
  }

  private redimensionner(conteneur: HTMLElement): void {
    const m = this.moteur
    if (!m) return
    const l = Math.max(1, Math.floor(conteneur.clientWidth))
    const h = Math.max(1, Math.floor(conteneur.clientHeight))
    m.rendu.setSize(l, h, false)
    m.camera.aspect = l / h
    // Sur un ecran etroit, on recule un peu pour garder le bonhomme entier.
    m.camera.position.z = m.camera.aspect < 0.85 ? 4.2 : 3.4
    m.camera.updateProjectionMatrix()
    m.camera.lookAt(0, 0.95, 0)
  }

  private brancherGlissement(toile: HTMLCanvasElement): void {
    let derniereX = 0
    let actif = false
    toile.addEventListener('pointerdown', (e) => {
      actif = true
      derniereX = e.clientX
      toile.setPointerCapture(e.pointerId)
      if (this.moteur) this.moteur.pauseJusqua = Number.POSITIVE_INFINITY
    })
    toile.addEventListener('pointermove', (e) => {
      if (!actif || !this.moteur) return
      this.moteur.angle += (e.clientX - derniereX) * 0.012
      derniereX = e.clientX
    })
    const relacher = (): void => {
      if (!actif) return
      actif = false
      if (this.moteur) this.moteur.pauseJusqua = performance.now() + PAUSE_APRES_GLISSEMENT
      journal.input('creation', 'rotation de l’apercu au doigt')
    }
    toile.addEventListener('pointerup', relacher)
    toile.addEventListener('pointercancel', relacher)
  }

  private arreterApercu(): void {
    const m = this.moteur
    if (!m) return
    this.moteur = null
    cancelAnimationFrame(m.image)
    m.observateur.disconnect()
    m.bonhomme.dispose()
    // Le socle ajoute par creerApercu n'appartient pas au bonhomme: on balaie
    // tout le groupe. dispose() est idempotent, un double appel ne coute rien.
    m.groupe.traverse((o) => {
      if (!(o instanceof THREE.Mesh)) return
      o.geometry.dispose()
      const mat: unknown = o.material
      if (Array.isArray(mat)) for (const x of mat as THREE.Material[]) x.dispose()
      else (mat as THREE.Material).dispose()
    })
    m.pivot.remove(m.groupe)
    m.scene.remove(m.pivot)
    m.rendu.dispose()
    // Liberer le contexte WebGL: le panneau peut etre reouvert souvent et les
    // navigateurs limitent le nombre de contextes vivants.
    m.rendu.forceContextLoss()
    m.toile.remove()
    journal.info('creation', 'apercu 3D libere')
  }
}
